import { useCallback, useLayoutEffect, useState } from "react";
import {
  FormContainer,
  IInputFile,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";
import { WhatsappFields } from "./WhatsappFields";
import { TButtonsList } from "../../../store/contexts/TemplateWhatsContext";
import { ISelectCountry } from "../../../components/UI/Inputs/AutoCompleteCountry/models";
import { countriesList } from "../../../services/country.service";
import {
  createWhatsappTemplate,
  getWhatsappTemplateById,
  updateWhatsappTemplateById,
} from "../../../services/whatsappTemplate.service";
import {
  IGetCharacterLimitRequest,
  IGetTicketAutomationTemplateMeta,
} from "../../../models/whatsapp-template";
import { BUTTONS_OPTIONS } from "../../../components/WhatsappTemplate/Buttons/SelectButtons/SelectButtons";
import { TWhatsappTemplateForm } from "./whatsappTemplateSchema";
import { IWhatsappTemplateProps } from "./models";
import { useWhatsappTemplateSchema } from "./useWhatsappTemplateSchema";
import { getNormalizedWhatsappActionTypes } from "../../../utils/ticketAutomation";

const translationPath = "chatBot.page.templates.whatsapp_template.";

const WhatsappTemplateFormScreen = ({ software }: IWhatsappTemplateProps) => {
  const { t } = useTranslation();

  const [status, setStatus] = useState<ISelectType>();

  const [buttonsOrder, setButtonsOrder] = useState<TButtonsList[]>([]);

  const [countries, setCountries] = useState<ISelectCountry[]>([]);

  const [templateFilesId, setTemplateFilesId] = useState<string>();

  const [body, setBody] = useState("");

  const { sendRequest } = useFetch(countriesList);

  const [deleteFilesList, setDeleteFilesList] = useState<string[]>([]);

  const { customWhatsappTemplateSchema } = useWhatsappTemplateSchema(software);

  const fetchCountries = useCallback(async () => {
    const { data, success } = await sendRequest(null);
    if (data && success) {
      setCountries(data);
    }
  }, [sendRequest]);

  const getButtonsOrder = useCallback((list: TButtonsList[]) => {
    setButtonsOrder(list);
  }, []);

  const { sendRequest: save, loading: sLoading } = useFetch(
    createWhatsappTemplate,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateWhatsappTemplateById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getWhatsappTemplateById,
  );

  const onDeleteFile = (file: IInputFile) => {
    setDeleteFilesList((prevState) => [...prevState, file.id]);
  };

  const saveTemplateJson = useCallback(
    (v: TWhatsappTemplateForm) => {
      const components = [];
      let headerType = "";

      if (v.headerType?.code === "1" && v.title && v.title.trim() !== "") {
        components.push({
          type: "HEADER",
          format: "TEXT",
          text: v.title,
        });
      } else if (v.headerType?.code === "2" && v.mediaFile) {
        headerType = "IMAGE";
      } else if (v.headerType?.code === "4" && v.mediaFile) {
        headerType = "DOCUMENT";
      }
      if (v.headerType?.code !== "1" && v.mediaFile) {
        components.push({
          type: "HEADER",
          format: headerType,
        });
      }

      components.push({
        type: "BODY",
        text: v.body,
        example:
          v.bodyExamples && v.bodyExamples.length > 0
            ? {
                body_text: [
                  v.bodyExamples
                    ? v.bodyExamples.map((v) => v.example)
                    : undefined,
                ],
              }
            : undefined,
      });

      if (v.footer && v.footer.trim() !== "") {
        components.push({
          type: "FOOTER",
          text: v.footer,
        });
      }

      const buttons =
        buttonsOrder[0] === "1"
          ? [
              ...(v.ctaButtons
                ? v.ctaButtons
                    .sort((a, b) =>
                      a.order < b.order ? -1 : a.order > b.order ? 1 : 0,
                    )
                    .map((btn) => ({
                      type: btn.actionType.value,
                      text: btn.actionType.id === "5" ? undefined : btn.text,
                      url: btn.actionType.id === "2" ? btn.payload : undefined,
                      example:
                        btn.urlType?.id === "2"
                          ? [btn.payload?.match(/\{\{(.+?)\}\}/)?.[1] || ""]
                          : btn.actionType.id === "5"
                            ? btn.payload
                            : undefined,
                      phone_number:
                        btn.actionType.id === "3"
                          ? `+${btn.country?.ddiCode}${btn.payload
                              ?.replaceAll("(", "")
                              ?.replaceAll(")", "")
                              ?.replaceAll("-", "")}`
                          : undefined,
                    }))
                : []),
              ...(v.customButtons
                ? v.customButtons
                    .sort((a, b) =>
                      a.order < b.order ? -1 : a.order > b.order ? 1 : 0,
                    )
                    .map((btn) => ({
                      type: "QUICK_REPLY",
                      text: btn.text,
                    }))
                : []),
            ]
          : [
              ...(v.customButtons
                ? v.customButtons
                    .sort((a, b) =>
                      a.order < b.order ? -1 : a.order > b.order ? 1 : 0,
                    )
                    .map((btn) => ({
                      type: "QUICK_REPLY",
                      text: btn.text,
                    }))
                : []),
              ...(v.ctaButtons
                ? v.ctaButtons
                    .sort((a, b) =>
                      a.order < b.order ? -1 : a.order > b.order ? 1 : 0,
                    )
                    .map((btn) => ({
                      type: btn.actionType.value,
                      text: btn.actionType.id === "5" ? undefined : btn.text,
                      url: btn.actionType.id === "2" ? btn.payload : undefined,
                      example:
                        btn.urlType?.id === "2"
                          ? [btn.payload?.match(/\{\{(.+?)\}\}/)?.[1] || ""]
                          : btn.actionType.id === "5"
                            ? btn.payload
                            : undefined,
                      phone_number:
                        btn.actionType.id === "3"
                          ? `+${btn.country?.ddiCode}${btn.payload
                              ?.replaceAll("(", "")
                              ?.replaceAll(")", "")
                              ?.replaceAll("-", "")}`
                          : undefined,
                    }))
                : []),
            ];

      if (buttons && buttons.length > 0) {
        components.push({
          type: "BUTTONS",
          buttons: buttons,
        });
      }
      const templateInJson = JSON.stringify({
        name: "",
        language: "pt_BR",
        category: v.whatsMetaCategory.name ?? "",
        allow_category_change: true,
        components: components,
      });

      return templateInJson;
    },
    [buttonsOrder],
  );

  const saveHandler = (v: TWhatsappTemplateForm) => {
    const marComFileFixed = software === "MarCom" ? v.fileFixed : null;
    const chatBotFileFixed =
      software === "ChatBot"
        ? v.ticketAutomation.code === "28" ||
          v.ticketAutomation.code === "29" ||
          v.ticketAutomation.code === "30"
          ? false
          : v.ticketAutomation.code === "8"
            ? typeof v.fileFixed === "boolean"
              ? v.fileFixed
              : false
            : true
        : false;

    return save({
      ticketServiceId: v.ticketService ? v.ticketService.id : "",
      ticketAutomationId: v.ticketAutomation.id,
      actionTypeId: v.actionType ? v.actionType.id : undefined,
      ticketAutomationInternalTemplateId: v.ticketAutomationInternalTemplate
        ? v.ticketAutomationInternalTemplate.id
        : null,
      name: v.name,
      enable: v.enable,
      characterLimitRequest: v.characterLimitRequests
        ? v.characterLimitRequests.map((item) => ({
            characterLimit: Number(item.characterLimit),
            ticketAutomationMacroId: item.ticketAutomationMacroId,
          }))
        : undefined,
      templateJson: saveTemplateJson(v),
      ticketAutomationTemplateHeaderFileTypeId: v.headerType
        ? v.headerType.id
        : undefined,
      mediaFilesIds: v.mediaFile ? [v.mediaFile.id] : undefined,
      deleteMediaFilesIds: deleteFilesList,
      fileFixed: marComFileFixed ?? chatBotFileFixed,
    });
  };

  const updateHandler = (v: TWhatsappTemplateForm, id: string) => {
    const mediaFilesIdsToSend =
      v.mediaFile?.id && v.mediaFile.id !== templateFilesId
        ? [v.mediaFile.id]
        : undefined;
    return update({
      id: id,
      item: {
        id: id,
        name: v.name,
        enable: v.enable,
        actionTypeId: v.actionType ? v.actionType.id : undefined,
        ticketAutomationInternalTemplateId: v.ticketAutomationInternalTemplate
          ? v.ticketAutomationInternalTemplate.id
          : null,
        templateJson: saveTemplateJson(v),
        ticketAutomationTemplateHeaderFileTypeId: v.headerType
          ? v.headerType.id
          : undefined,
        mediaFilesIds: mediaFilesIdsToSend,
        deleteMediaFilesIds: deleteFilesList,
        characterLimitRequest: v.characterLimitRequests
          ? v.characterLimitRequests.map((item) => ({
              characterLimit: Number(item.characterLimit),
              ticketAutomationMacroId: item.ticketAutomationMacroId,
            }))
          : undefined,
      },
    });
  };

  const getPlaceholderCounts = (text?: string) => {
    const counts: Record<string, number> = {};
    if (!text) return counts;

    const re = /{{\s*([^}]+?)\s*}}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const name = m[1].trim();
      counts[name] = (counts[name] ?? 0) + 1;
    }
    return counts;
  };

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TWhatsappTemplateForm>> => {
      const { data, success } = await get(id);

      if (data) {
        setStatus(data.status);

        const templateJson =
          data.ticketAutomationTemplateMetas.length > 0
            ? JSON.parse(
                data.ticketAutomationTemplateMetas[
                  data.ticketAutomationTemplateMetas.length - 1
                ].content,
              )
            : [];
        const templateMediaFile =
          data.ticketAutomationTemplateMetas.length > 0
            ? data.ticketAutomationTemplateMetas[
                data.ticketAutomationTemplateMetas.length - 1
              ].ticketAutomationTemplateMetaMediaFiles[0]
            : ({} as IGetTicketAutomationTemplateMeta);
        console.log("templateMediaFile:", templateMediaFile);
        const templateHeaderType =
          data.ticketAutomationTemplateMetas.length > 0
            ? data.ticketAutomationTemplateMetas[
                data.ticketAutomationTemplateMetas.length - 1
              ].ticketAutomationTemplateHeaderFileType
            : ({} as ISelectType);

        const ticketAutomationTemplateMacros =
          data.ticketAutomationTemplateMetas.length > 0
            ? data.ticketAutomationTemplateMetas[
                data.ticketAutomationTemplateMetas.length - 1
              ].ticketAutomationTemplateMacrosVariables
            : [];

        const components: any[] =
          "components" in templateJson ? templateJson.components : [];

        const buttons = components.find((c) => c.type === "BUTTONS")?.buttons;
        const body = components.find((c) => c.type === "BODY");

        setBody(body?.text || "");

        if (
          buttons &&
          buttons.length > 0 &&
          buttons[0].type === "QUICK_REPLY"
        ) {
          setButtonsOrder(["2", "1"]);
        }

        const placeholderCounts = getPlaceholderCounts(body?.text);

        const newData: TWhatsappTemplateForm = {
          ...data,
          invalidVariables: false,
          fileFixed: data.ticketAutomationTemplateMetas[0].fileFixed,
          ticketAutomation: {
            id: data.ticketAutomation.id,
            name: data.ticketAutomation.name,
            code: data.ticketAutomation.code ?? null,
            actionTypes: getNormalizedWhatsappActionTypes(
              data.ticketAutomation.code,
              (data.ticketAutomation.actionTypes ?? []).map((a) => ({
                id: a.id,
                name: a.name,
                code: a.code,
                description: a.description,
              })),
            ),
          },
          title: components.find((c) => c.type === "HEADER")?.text || "",
          whatsMetaCategory: {
            id: templateJson?.category,
            name: templateJson?.category,
          },
          mediaFile: templateMediaFile
            ? {
                id: templateMediaFile.id,
                mediaTypeId: templateMediaFile.mediaTypeId,
                isPublic: templateMediaFile.isPublic,
                fileName: templateMediaFile.fileName,
                filePath: templateMediaFile.filePath,
                mimeType: templateMediaFile.mimeType,
                fileSize: templateMediaFile.fileSize,
                mediaType: templateMediaFile.mediaType,
                cdnDomain: templateMediaFile.cdnDomain,
              }
            : null,
          body: body ? body.text : "",
          headerType: templateHeaderType,
          bodyExamples:
            body && body.example && body.example.body_text
              ? body.example.body_text[0].map((e: string) => ({ example: e }))
              : [],

          characterLimitRequests: ticketAutomationTemplateMacros
            ? ticketAutomationTemplateMacros.flatMap(
                (item: IGetCharacterLimitRequest) => {
                  const count = placeholderCounts[item.name] ?? 0;
                  if (count <= 0) return [];
                  return Array.from({ length: count }, () => ({
                    ticketAutomationMacroId: item.id,
                    name: item.name,
                    characterLimit:
                      item.characterLimit != null
                        ? String(item.characterLimit)
                        : "",
                  }));
                },
              )
            : [],

          footer: components.find((c) => c.type === "FOOTER")?.text || "",
          customButtons:
            buttons &&
            buttons
              .filter((btn: any) => btn.type === "QUICK_REPLY")
              .map((btn: any, index: number) => ({
                id: index.toString(),
                order: index,
                text: btn.text,
              })),
          ctaButtons:
            (buttons &&
              buttons
                .filter((b: any) => b.type !== "QUICK_REPLY")
                .map((btn: any, index: number) => {
                  const country = btn.phone_number
                    ? countries.find(
                        (country) =>
                          country.ddiCode === btn.phone_number?.substring(1, 3),
                      )
                    : null;

                  const button = {
                    id: index.toString(),
                    order: index,
                    text: btn.text,
                    actionType: BUTTONS_OPTIONS.find(
                      (o) => o.value === btn.type,
                    ),
                    payload:
                      btn.url ||
                      btn.example ||
                      (btn.phone_number ? btn.phone_number.substring(3) : "") ||
                      "",
                  };

                  return country ? { ...button, country } : button;
                })) ||
            undefined,
        };
        if (templateMediaFile) {
          const filesId = templateMediaFile.id;
          if (filesId) setTemplateFilesId(filesId);
        }
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get, countries],
  );
  const loading = sLoading || uLoading || gLoading;

  useLayoutEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  return (
    <FormContainer<TWhatsappTemplateForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      defaultValues={{
        whatsMetaCategory: {
          id: "UTILITY",
          name: "UTILITY",
        },
        invalidVariables: false,
      }}
      disableSave={status && status.code === "2"}
      title={t(translationPath + "whatsapp_template")}
      schema={customWhatsappTemplateSchema}
      subtitle={t(translationPath + "new_whatsapp_template")}
      subtitleWatchField={"name"}
      fields={() => (
        <WhatsappFields
          status={status}
          buttonsOrder={buttonsOrder}
          body={body}
          onGetButtonsOrder={getButtonsOrder}
          onDeleteFile={onDeleteFile}
          software={software}
        />
      )}
    />
  );
};

export default WhatsappTemplateFormScreen;
